<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>
            <div class="page-head">
              <h2>Canceled dancers</h2>
            </div>

            <p class="muted small">
              Dancers who pulled out of this year. Kept in a separate collection so the active dancers UI doesn't have to
              filter them — but their emails still match against Stripe transactions for deposits we're holding for next year.
            </p>

            <p v-if="loading" class="muted">Loading…</p>

            <template v-else>
              <div class="list-head">
                <h3 class="section-h">{{ canceled.length }} canceled dancer{{ canceled.length === 1 ? '' : 's' }}</h3>
                <div class="copy-emails-wrap">
                  <v-btn
                    small
                    outlined
                    color="primary"
                    :disabled="!emailList.length"
                    @click="copyAllEmails"
                  >
                    Copy all emails
                  </v-btn>
                  <span v-if="copyFeedback" class="copy-feedback" role="status">{{ copyFeedback }}</span>
                </div>
              </div>
              <p v-if="emailList.length" class="muted small copy-hint">
                {{ emailList.length }} unique email{{ emailList.length === 1 ? '' : 's' }} — one per line (good for BCC).
              </p>
              <p v-if="usedFor2027Count" class="muted small copy-hint">
                {{ usedFor2027Count }} used their deposit for IABF {{ registrationYear }}.
              </p>
              <table class="ledger-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th class="num-col">Deposit on file</th>
                    <th>Deposit credit</th>
                    <th class="num-col">Payments paid</th>
                    <th>Notes</th>
                    <th>Canceled</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in sortedCanceled" :key="d.id">
                    <td><strong>{{ d.firstName }} {{ d.lastName }}</strong></td>
                    <td>
                      <div>{{ d.email }}</div>
                      <button
                        type="button"
                        class="link-btn"
                        @click="openEmailIdentity(d)"
                      >Link emails</button>
                    </td>
                    <td class="num-col">{{ fmtMoney(d.depositOnFileCents) }}</td>
                    <td>
                      <span
                        class="credit-pill"
                        :class="creditPillClass(d)"
                      >{{ creditLabel(d) }}</span>
                    </td>
                    <td class="num-col">{{ fmtMoney(d.paymentsOnFileCents) }}</td>
                    <td class="muted small">{{ d.notes || '—' }}</td>
                    <td class="muted small">{{ formatDate(d.canceledAt) }}</td>
                    <td class="actions-col">
                      <button
                        v-if="d.snapshot"
                        type="button"
                        class="link-btn"
                        :disabled="restoringId === d.id"
                        @click="onRestore(d)"
                      >Restore</button>
                      <button
                        type="button"
                        class="link-btn link-btn-danger"
                        @click="onPermanentDelete(d)"
                      >Delete</button>
                    </td>
                  </tr>
                  <tr v-if="!canceled.length">
                    <td colspan="8" class="muted small">No canceled dancers yet.</td>
                  </tr>
                </tbody>
              </table>

            </template>

            <v-dialog v-model="emailIdentityOpen" width="560">
              <v-card v-if="emailIdentityRow">
                <v-card-title class="text-h5">
                  Link emails — {{ emailIdentityRow.firstName }} {{ emailIdentityRow.lastName }}
                </v-card-title>
                <v-card-text>
                  <iabf-person-email-identity
                    :key="emailIdentityPersonId + '|' + emailIdentityRow.email"
                    :person-id="emailIdentityPersonId"
                    :email="emailIdentityRow.email"
                    @updated="onEmailIdentityUpdated"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn text @click="emailIdentityOpen = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { IABF_REGISTRATION_YEAR } from '@/config/iabf-event'
import IabfPersonEmailIdentity from '@/components/iabf/IabfPersonEmailIdentity'

export default {
  components: { IabfPersonEmailIdentity },
  data () {
    return {
      emailIdentityOpen: false,
      emailIdentityRow: null,
      loading: true,
      unsubCanceled: null,
      restoringId: null,
      copyFeedback: '',
      copyFeedbackTimer: null
    }
  },
  computed: {
    canceled () {
      return this.$store.getters.canceledDancers || []
    },
    sortedCanceled () {
      return this.canceled
        .slice()
        .sort((a, b) => (b.canceledAt || 0) - (a.canceledAt || 0))
    },
    emailList () {
      const seen = new Set()
      const out = []
      for (const d of this.sortedCanceled) {
        const e = String((d && d.email) || '').trim().toLowerCase()
        if (!e || seen.has(e)) continue
        seen.add(e)
        out.push(e)
      }
      return out
    },
    registrationYear () {
      return IABF_REGISTRATION_YEAR
    },
    usedFor2027Count () {
      return this.canceled.filter((d) => this.usedDepositForRegistrationYear(d)).length
    },
    emailIdentityPersonId () {
      const row = this.emailIdentityRow || {}
      return row.personId || ''
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    try {
      this.unsubCanceled = await this.$store.dispatch('subscribeCanceledDancers')
    } catch (e) {
      console.error('subscribeCanceledDancers failed:', e)
    }
    this.loading = false
  },
  beforeDestroy () {
    if (typeof this.unsubCanceled === 'function') this.unsubCanceled()
  },
  methods: {
    fmtMoney (cents) {
      const dollars = (Math.round(Number(cents) || 0) / 100)
      return '$' + dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDate (ts) {
      if (!ts) return ''
      const d = new Date(ts)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },
    usedDepositForRegistrationYear (d) {
      if (!d || !d.depositCreditConsumed) {
        return false
      }
      const y = Number(d.depositCreditConsumedYear)
      if (Number.isFinite(y) && y > 0) {
        return y === this.registrationYear
      }
      return true
    },
    creditLabel (d) {
      if (this.usedDepositForRegistrationYear(d)) {
        const when = this.formatDate(d.depositCreditConsumedAt)
        return when
          ? ('Used for ' + this.registrationYear + ' · ' + when)
          : ('Used for ' + this.registrationYear)
      }
      if ((Number(d.depositOnFileCents) || 0) > 0) {
        return 'Still held'
      }
      return '—'
    },
    creditPillClass (d) {
      if (this.usedDepositForRegistrationYear(d)) {
        return 'is-used'
      }
      if ((Number(d.depositOnFileCents) || 0) > 0) {
        return 'is-held'
      }
      return 'is-none'
    },
    openEmailIdentity (row) {
      this.emailIdentityRow = row
      this.emailIdentityOpen = true
    },
    onEmailIdentityUpdated (result) {
      if (this.emailIdentityRow && result && result.email) {
        this.$set(this.emailIdentityRow, 'email', result.email)
      }
    },
    async copyAllEmails () {
      const text = this.emailList.join('\n')
      if (!text) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.setAttribute('readonly', '')
          ta.style.position = 'fixed'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copyFeedback = `Copied ${this.emailList.length} email${this.emailList.length === 1 ? '' : 's'}`
      } catch (e) {
        console.error('copy emails failed', e)
        this.copyFeedback = 'Could not copy — select emails from the table instead.'
      }
      if (this.copyFeedbackTimer) clearTimeout(this.copyFeedbackTimer)
      this.copyFeedbackTimer = setTimeout(() => {
        this.copyFeedback = ''
      }, 2500)
    },
    async onRestore (d) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Restore ${d.firstName} ${d.lastName} (${d.email}) to the active dancers list?`)) return
      this.restoringId = d.id
      try {
        await this.$store.dispatch('restoreCanceledDancer', d.id)
      } catch (e) {
        console.error('restore failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Restore failed: ' + (e && e.message ? e.message : e))
      }
      this.restoringId = null
    },
    async onPermanentDelete (d) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Permanently delete the canceled record for ${d.firstName} ${d.lastName} (${d.email})? Their email will no longer be matched in Stripe sync.`)) return
      try {
        await this.$store.dispatch('deleteCanceledDancer', d.id)
      } catch (e) {
        console.error('delete failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Delete failed: ' + (e && e.message ? e.message : e))
      }
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 4px; }
h2 { font-family: Quattrocento, Georgia, serif; font-size: 28px; margin: 0; }
.muted { color: #888; font-style: italic; }
.small { font-size: 13px; }
.list-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  margin-top: 8px;
}
.list-head .section-h { margin: 0; }
.copy-emails-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.copy-feedback {
  font-size: 13px;
  color: #2f6b4f;
  font-style: normal;
  font-weight: 600;
}
.copy-hint { margin: 4px 0 10px; }

.section-h {
  font-family: Quattrocento, Georgia, serif;
  font-size: 18px;
  margin: 24px 0 8px;
  border-bottom: 1px solid #eee;
  padding-bottom: 4px;
}

.ledger-table { width: 100%; border-collapse: collapse; font-family: 'Fanwood Text', Georgia, serif; font-size: 14px; }
.ledger-table th, .ledger-table td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #f1eee9; vertical-align: top; }
.ledger-table th { background: #f7f5ef; font-family: Quattrocento, Georgia, serif; font-size: 12px; letter-spacing: 0.04em; }
.num-col { text-align: right; white-space: nowrap; }
.actions-col { white-space: nowrap; }

.credit-pill {
  display: inline-block;
  font-size: 12px;
  font-style: normal;
  font-family: Quattrocento, Georgia, serif;
  letter-spacing: 0.02em;
  padding: 3px 8px;
  border-radius: 999px;
  white-space: nowrap;
}
.credit-pill.is-used {
  background: #eef0eb;
  color: #3d4a38;
}
.credit-pill.is-held {
  background: #f4f0e6;
  color: #6b5e3c;
}
.credit-pill.is-none {
  color: #888;
  padding: 0;
}

.link-btn {
  background: none;
  border: none;
  color: #267E7D;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  padding: 0 4px;
  text-decoration: underline;
}
.link-btn:hover { color: #2f9695; }
.link-btn-danger { color: #c0392b; }
.link-btn-danger:hover { color: #a83228; }

code { background: #f4f0e6; padding: 1px 5px; border-radius: 3px; font-size: 12px; }
</style>
</content>
</invoke>